// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * One line of text for what a position held at some moment.
 *
 * Shared by the version comparison and the maintenance detail, because both answer the same
 * question — "what was in this position, before and after" — and two renderings of it would
 * drift: one showing `× 20` and the other `20`, or one reading `serial_raw` and the other
 * missing that snapshots store `serial_normalized`.
 *
 * A serialized position is its serial; a counted one has no serial, and its count is the
 * whole content of the change (§6.3).
 */

import type { ArchiveItem } from "@/api/types";

export type PartSide = Record<string, unknown> | null | undefined;

/** 物料描述 of what is in a position — the substitute's after a replacement that gave one,
 *  else the position's own name. One rule, so the list and the dialogs never disagree. */
export function materialInfoOf(item: Pick<ArchiveItem, "material_info" | "position_name">) {
  return item.material_info || item.position_name;
}

export function summarizePart(side: PartSide): string {
  if (!side) return "—";
  const serial = side.serial_raw ?? side.serial_normalized;
  if (typeof serial === "string" && serial) return serial;
  return side.quantity === undefined || side.quantity === null ? "—" : `× ${String(side.quantity)}`;
}

/** The material a side names, or null when it names none. */
export function materialOf(side: PartSide): string | null {
  if (!side) return null;
  const code = typeof side.material_code === "string" ? side.material_code : "";
  const name = typeof side.material_name === "string" ? side.material_name : "";
  return [code, name].filter(Boolean).join(" · ") || null;
}

/** Whether a position went from one material to another — a substitute fitted, or the BOM's
 *  material back in place of one (§8.6.1). Only between two sides that both hold something:
 *  a position that appears or goes is a different kind of change. */
export function materialChangedBetween(before: PartSide, after: PartSide): boolean {
  return !!before && !!after && materialOf(before) !== materialOf(after);
}
